import React, { useState, useMemo } from "react";
import { Search, Plus, Download, Filter, MoreHorizontal, Edit3, Trash2, Eye, ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/layout/PageShell";
import { cn } from "@/lib/utils";

export function DataToolbar({
  searchValue,
  onSearch,
  onAdd,
  addLabel = "Create New",
  extra,
  testIdPrefix = "dt",
}) {
  return (
    <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
      <div className="flex items-center gap-2 flex-1 min-w-0 sm:min-w-[200px] max-w-full sm:max-w-[420px] w-full sm:w-auto">
        <div className="flex items-center bg-white border border-[#E5E7EB] rounded-md h-9 px-2.5 w-full focus-within:ring-2 focus-within:ring-blue-500/20">
          <Search size={14} className="text-slate-400 mr-2" />
          <Input
            data-testid={`${testIdPrefix}-search`}
            value={searchValue}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search…"
            className="border-0 shadow-none focus-visible:ring-0 h-7 px-0 bg-transparent text-[13px]"
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        {extra}
        <Button variant="outline" size="sm" className="h-9" data-testid={`${testIdPrefix}-export`}>
          <Download size={14} className="mr-1.5" /> Export
        </Button>
        {onAdd && (
          <Button
            size="sm"
            className="h-9 bg-[#2563EB] hover:bg-blue-700"
            onClick={onAdd}
            data-testid={`${testIdPrefix}-add`}
          >
            <Plus size={14} className="mr-1.5" /> {addLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export function DataTable({
  columns,
  rows,
  testIdPrefix = "dt",
  rowActions,
  emptyText = "No records",
  onRowClick,
  defaultSort = null,
  pagination = null,
}) {
  const [sortKey, setSortKey] = useState(defaultSort?.key || null);
  const [sortDir, setSortDir] = useState(defaultSort?.dir || "asc");

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = col?.sortValue ? col.sortValue(a) : a[sortKey];
      const bv = col?.sortValue ? col.sortValue(b) : b[sortKey];
      const cmp = compareValues(av, bv);
      return sortDir === "desc" ? -cmp : cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir, columns]);

  function toggleSort(col) {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(col.key);
    setSortDir("asc");
  }

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden min-w-0" data-testid={`${testIdPrefix}-table`}>
      <div className="overflow-x-auto max-w-full">
        <table className="w-full text-[12.5px]">
          <thead className="bg-slate-50 text-slate-500 border-b">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    "font-medium px-4 py-2.5 whitespace-nowrap",
                    c.align === "right" ? "text-right" : "text-left",
                    c.sortable && "cursor-pointer select-none hover:text-slate-800",
                  )}
                  style={c.width ? { width: c.width } : undefined}
                  onClick={() => toggleSort(c)}
                >
                  <span className={cn("inline-flex items-center gap-1", c.align === "right" && "justify-end w-full")}>
                    {c.label}
                    {c.sortable ? (
                      sortKey === c.key ? (
                        sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                      ) : (
                        <ArrowUpDown size={12} className="opacity-40" />
                      )
                    ) : null}
                  </span>
                </th>
              ))}
              {rowActions && <th className="w-12 px-2 py-2.5" />}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 && (
              <tr>
                <td colSpan={columns.length + (rowActions ? 1 : 0)} className="text-center text-slate-400 py-10">
                  {emptyText}
                </td>
              </tr>
            )}
            {sortedRows.map((row, i) => (
              <tr
                key={row.id || i}
                className={cn("border-t hover:bg-slate-50/60", onRowClick && "cursor-pointer")}
                data-testid={`${testIdPrefix}-row-${row.id || i}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn(
                      "px-4 py-2.5 align-middle",
                      c.align === "right" ? "text-right" : "text-left",
                      c.mono && "font-mono",
                      c.bold && "font-semibold text-slate-900",
                    )}
                  >
                    {c.render ? c.render(row[c.key], row) : row[c.key]}
                  </td>
                ))}
                {rowActions && (
                  <td className="px-2 py-2">
                    <div className="flex justify-end gap-0.5">
                      {rowActions(row).map((a, idx) => (
                        <Button
                          key={idx}
                          variant="ghost"
                          size="sm"
                          className={cn("h-7 w-7 p-0 text-slate-500 hover:text-slate-900", a.danger && "hover:text-red-600")}
                          title={a.title}
                          onClick={(e) => {
                            e.stopPropagation();
                            a.onClick?.(e);
                          }}
                          data-testid={`${testIdPrefix}-${a.title?.toLowerCase()}-${row.id}`}
                        >
                          {a.icon}
                        </Button>
                      ))}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2.5 border-t bg-slate-50/60 flex items-center justify-between text-[11.5px] text-slate-500">
        <span>
          {pagination
            ? <>Showing <strong className="text-slate-700">{pagination.from ?? 0}</strong>–<strong className="text-slate-700">{pagination.to ?? 0}</strong> of <strong className="text-slate-700">{pagination.total ?? 0}</strong></>
            : <>Showing <strong className="text-slate-700">{sortedRows.length}</strong> records</>}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[11px]"
            disabled={!pagination || pagination.page <= 1}
            onClick={() => pagination?.onPageChange?.(pagination.page - 1)}
          >
            Previous
          </Button>
          <span className="px-2">
            Page {pagination?.page ?? 1} of {pagination?.lastPage ?? 1}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[11px]"
            disabled={!pagination || pagination.page >= (pagination.lastPage || 1)}
            onClick={() => pagination?.onPageChange?.(pagination.page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

export const defaultActions = (testIdPrefix = "dt") => (row) => [
  { title: "View", icon: <Eye size={14} />, onClick: () => {} },
  { title: "Edit", icon: <Edit3 size={14} />, onClick: () => {} },
  { title: "Delete", icon: <Trash2 size={14} />, danger: true, onClick: () => {} },
];
